// Shared helper: derive a job's order destination (stock vs client), the
// reserved-for-client vs planned-for-stock split, client names, due dates and
// each allocated order line's pallet plan. Used by the machine-queue and
// project-dashboard tables so they show consistent client/stock/pallet info.

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Build lookups from the orders list (useOrders()).
export function buildClientIndex(orders = []) {
  const byOrder = {}; // orderId -> { client, dueDate, orderCode }
  const byLine = {}; // `${orderId}::${lineId}` -> { client, orderCode, dueDate, palletPlan, product }
  const byItem = {}; // deliveryPlan item id -> { client, orderCode, dueDate }
  (Array.isArray(orders) ? orders : []).forEach(o => {
    if (!o?.orderId) return;
    byOrder[o.orderId] = {
      client: o.client || null,
      dueDate: o.dueDate || null,
      orderCode: o.orderCode || null
    };
    (Array.isArray(o.lines) ? o.lines : []).forEach(l => {
      const info = {
        client: o.client || null,
        orderCode: o.orderCode || null,
        dueDate: o.dueDate || null
      };
      byLine[`${o.orderId}::${l.lineId}`] = {
        ...info,
        palletPlan: Array.isArray(l.palletPlan) ? l.palletPlan : [],
        product: l.product || null
      };
      (Array.isArray(l?.deliveryPlan?.items) ? l.deliveryPlan.items : []).forEach(
        it => {
          if (it?.itemId) byItem[it.itemId] = { ...info };
        }
      );
    });
  });
  return { byOrder, byLine, byItem };
}

// Derive destination info from a job's allocations + plannedLength.
export function deriveDestination(allocations, plannedLength, index = {}) {
  const allocs = Array.isArray(allocations) ? allocations : [];
  const byOrder = index.byOrder || {};
  const byLine = index.byLine || {};

  const allocatedToOrders = allocs.reduce(
    (s, a) => s + num(a?.allocatedMeters),
    0
  );
  const planned = num(plannedLength);
  const stockMeters = Math.max(0, planned - allocatedToOrders);

  let destination = 'STOCK';
  if (allocatedToOrders > 0 && stockMeters > 0) destination = 'MIXED';
  else if (allocatedToOrders > 0) destination = 'CLIENT';

  const lines = allocs.map(a => {
    const li = byLine[`${a?.orderId}::${a?.lineId}`] || byOrder[a?.orderId] || {};
    return {
      orderId: a?.orderId || null,
      lineId: a?.lineId || null,
      meters: num(a?.allocatedMeters),
      client: li.client || null,
      orderCode: li.orderCode || null,
      dueDate: li.dueDate || null,
      palletPlan: Array.isArray(li.palletPlan) ? li.palletPlan : []
    };
  });

  const clients = [...new Set(lines.map(l => l.client).filter(Boolean))];
  const dueDates = [...new Set(lines.map(l => l.dueDate).filter(Boolean))].sort();

  return {
    destination, // 'CLIENT' | 'STOCK' | 'MIXED'
    clients,
    allocatedToOrders,
    stockMeters,
    plannedLength: planned,
    lines,
    dueEarliest: dueDates[0] || null
  };
}

// Compact pallet-plan summary, e.g. "5×100 (×3)" -> "3 pallets · 5 coils/pallet".
export function summarizePalletPlan(palletPlan = []) {
  const rows = Array.isArray(palletPlan) ? palletPlan : [];
  const totalPallets = rows.reduce((s, p) => s + num(p?.palletCount), 0);
  if (!totalPallets) return '';
  const parts = rows
    .filter(p => num(p?.palletCount) > 0)
    .map(p => {
      const cpp = num(p.coilsPerPallet);
      const cl = num(p.coilLength);
      const pc = num(p.palletCount);
      return `${pc}×[${cpp} coil${cpp === 1 ? '' : 's'}${cl ? ` @${cl}m` : ''}]`;
    });
  return parts.join(' + ');
}

/**
 * One colour vocabulary for where a product's cable is going.
 *
 * Three screens ask this question — Project Bobin State, the machine queue, and
 * "Where can this cable go?" — and each had grown its own palette. Stock was
 * grey in all three, which is what prompted this, but underneath they also
 * disagreed with each other: an order was `primary` on one and `info` on the
 * next, mixed was `warning` here and `info` there. Somebody moving between them
 * had to relearn the colours each time.
 *
 * Stock is green because it is the state a planner is hunting for — cable
 * nobody has claimed, free to promise. Grey said the opposite: nothing here.
 *
 * WAREHOUSE only exists on the allocation table, where a product that has left
 * production entirely must not be confused with one that is merely unpromised.
 */
export const DESTINATION_BADGE = {
  ORDER: { bg: 'primary', label: 'Order' },
  CLIENT: { bg: 'primary', label: 'Client' },
  MIXED: { bg: 'warning', label: 'Mixed' },
  STOCK: { bg: 'success', label: 'Stock' },
  WAREHOUSE: { bg: 'info', label: 'In warehouse' }
};

export const destinationBadge = kind =>
  DESTINATION_BADGE[String(kind || '').toUpperCase()] || DESTINATION_BADGE.STOCK;
